import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Grid, List } from 'lucide-react';

interface ProductImage {
  id: string;
  src: string;
  alt: string;
  category: string;
  title: string;
  description: string;
}

const ProductGallery: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<ProductImage | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const productImages: ProductImage[] = [
    {
      id: '1',
      src: '/img/product/Bromine Bottle.jpg',
      alt: 'Bromine Bottle',
      category: 'Bromine',
      title: 'Liquid Bromine Bottle',
      description: 'High-purity liquid bromine in professional packaging'
    },
    {
      id: '2',
      src: '/img/product/Bromine Carton.jpg',
      alt: 'Bromine Carton',
      category: 'Bromine',
      title: 'Bromine Carton Packaging',
      description: 'Bulk packaging for industrial bromine supply'
    },
    {
      id: '3',
      src: '/img/product/Bromine Plant.jpg',
      alt: 'Bromine Plant',
      category: 'Bromine',
      title: 'Bromine Manufacturing Plant',
      description: 'State-of-the-art bromine production facility'
    },
    {
      id: '4',
      src: '/img/product/Bromine Carton 2.jpg',
      alt: 'Bromine Carton 2',
      category: 'Bromine',
      title: 'Bromine Industrial Packaging',
      description: 'Professional packaging for industrial applications'
    },
    {
      id: '5',
      src: '/img/product/Magnesium Chloride Flakes.jpg',
      alt: 'Magnesium Chloride Flakes',
      category: 'Magnesium Chloride',
      title: 'Magnesium Chloride Flakes',
      description: 'High-purity magnesium chloride flakes'
    },
    {
      id: '6',
      src: '/img/product/Magnesium Chloride Flakes 2.jpg',
      alt: 'Magnesium Chloride Flakes 2',
      category: 'Magnesium Chloride',
      title: 'Magnesium Chloride Product',
      description: 'Premium quality magnesium chloride for various applications'
    },
    {
      id: '7',
      src: '/img/product/Magnesium Chloride Flakes 3.jpg',
      alt: 'Magnesium Chloride Flakes 3',
      category: 'Magnesium Chloride',
      title: 'Magnesium Chloride Packaging',
      description: 'Professional packaging for magnesium chloride products'
    },
    {
      id: '8',
      src: '/img/product/Bromine Specifications.jpg',
      alt: 'Bromine Specifications',
      category: 'Bromine',
      title: 'Bromine Product Specifications',
      description: 'Detailed specifications and quality parameters'
    }
  ];

  const categories = ['All', 'Bromine', 'Magnesium Chloride'];

  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredImages = selectedCategory === 'All' 
    ? productImages 
    : productImages.filter(img => img.category === selectedCategory);

  const openModal = (image: ProductImage, index: number) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentImageIndex + 1) % filteredImages.length;
    setSelectedImage(filteredImages[nextIndex]);
    setCurrentImageIndex(nextIndex);
  };

  const prevImage = () => {
    const prevIndex = currentImageIndex === 0 ? filteredImages.length - 1 : currentImageIndex - 1;
    setSelectedImage(filteredImages[prevIndex]);
    setCurrentImageIndex(prevIndex);
  };

  return (
    <div id="gallery" className="section-padding bg-gray-50 dark:bg-gray-800">
      <div className="container-custom">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="mb-4">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Product Gallery
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Explore our manufacturing facilities and product packaging
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors duration-200 ${
                  viewMode === 'grid' 
                    ? 'bg-white dark:bg-gray-600 text-chemical-600 dark:text-chemical-400 shadow-sm' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors duration-200 ${
                  viewMode === 'list' 
                    ? 'bg-white dark:bg-gray-600 text-chemical-600 dark:text-chemical-400 shadow-sm' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-8 max-sm:mt-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                selectedCategory === category
                  ? 'bg-chemical-600 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-chemical-100 dark:hover:bg-chemical-900/20 hover:text-chemical-700 dark:hover:text-chemical-400 border border-gray-200 dark:border-gray-600'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        {viewMode === 'grid' ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredImages.map((image, index) => (
              <div
                key={image.id}
                className="bg-white dark:bg-gray-700 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer group"
                onClick={() => openModal(image, index)}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/img/icons/logo512.png';
                    }}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white dark:bg-gray-800 rounded-full p-3">
                        <div className="w-6 h-6 text-chemical-600 dark:text-chemical-400 flex items-center justify-center">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-chemical-600 dark:text-chemical-400 bg-chemical-100 dark:bg-chemical-900/20 px-3 py-1 rounded-full">
                      {image.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {image.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {image.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="space-y-6">
            {filteredImages.map((image, index) => (
              <div
                key={image.id}
                className="bg-white dark:bg-gray-700 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer group"
                onClick={() => openModal(image, index)}
              >
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/3 relative aspect-[4/3] md:aspect-auto">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/img/icons/logo512.png';
                      }}
                    />
                  </div>
                  <div className="md:w-2/3 p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-chemical-600 dark:text-chemical-400 bg-chemical-100 dark:bg-chemical-900/20 px-3 py-1 rounded-full">
                        {image.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {image.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      {image.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-6xl w-full max-h-[90vh]">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 bg-white dark:bg-gray-800 rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            </button>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden max-h-[90vh] flex flex-col">
              <div className="relative flex-1 min-h-0">
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  className="w-full h-full object-contain bg-gray-50 dark:bg-gray-900"
                  style={{ maxHeight: '70vh' }}
                />
              </div>
              
              <div className="p-6 flex-shrink-0">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-chemical-600 dark:text-chemical-400 bg-chemical-100 dark:bg-chemical-900/20 px-3 py-1 rounded-full">
                    {selectedImage.category}
                  </span>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {currentImageIndex + 1} of {filteredImages.length}
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {selectedImage.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {selectedImage.description}
                </p>
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white dark:bg-gray-800 rounded-full p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <ChevronLeft className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white dark:bg-gray-800 rounded-full p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <ChevronRight className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductGallery; 